'use client';

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { SupportButton } from './SupportLink';

type State = 'off' | 'on' | 'busy' | 'denied' | 'unsupported' | 'need-install';

function urlB64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const arr = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// iOS only allows Web Push from an installed (home-screen) PWA.
function iosNeedsInstall(): boolean {
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return isIOS && !standalone;
}

// A small iOS-style toggle switch.
function Switch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-600'
      } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// Master "手機推播" switch. Push is a paid-member perk: free members see the
// switch off + disabled with a 了解方案 link that opens a details modal; paid
// members can freely turn push delivery on/off (subscribe / unsubscribe).
export default function NotificationToggle() {
  const [paid, setPaid] = useState<boolean | null>(null); // null = loading
  const [state, setState] = useState<State>('off');
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(false);

  useEffect(() => {
    api
      .usage()
      .then((u) => setPaid(u.plan !== 'free'))
      .catch(() => setPaid(null));
  }, []);

  // For paid members, reflect the current subscription state.
  useEffect(() => {
    if (paid !== true) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState(iosNeedsInstall() ? 'need-install' : 'unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setState(sub && Notification.permission === 'granted' ? 'on' : 'off'))
      .catch(() => setState('off'));
  }, [paid]);

  async function subscribe() {
    const reg = await navigator.serviceWorker.ready;
    const { public_key } = await api.pushVapid();
    const appKey = urlB64ToUint8Array(public_key);
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      const cur = sub.options?.applicationServerKey;
      const matches =
        cur && new Uint8Array(cur).every((b, i) => b === appKey[i]) && cur.byteLength === appKey.length;
      if (!matches) {
        await sub.unsubscribe();
        sub = null;
      }
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
    const j = sub.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
    if (!j.endpoint || !j.keys) throw new Error('subscription incomplete');
    await api.pushSubscribe({ endpoint: j.endpoint, keys: j.keys });
  }

  async function unsubscribe() {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    const endpoint = sub?.endpoint;
    if (sub) await sub.unsubscribe();
    await api.pushUnsubscribe(endpoint);
  }

  async function toggle() {
    if (state === 'busy') return;
    setErr('');
    if (iosNeedsInstall()) {
      setState('need-install');
      return;
    }
    const turningOn = state !== 'on';
    setState('busy');
    try {
      if (turningOn) {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          setState(perm === 'denied' ? 'denied' : 'off');
          return;
        }
        await subscribe();
        setState('on');
      } else {
        await unsubscribe();
        setState('off');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : '操作失敗');
      setState(turningOn ? 'off' : 'on');
    }
  }

  if (paid === null) return null; // plan still loading — avoid a flash

  // ---- Free member: disabled switch + warning + details modal ----
  if (!paid) {
    return (
      <>
        <div className="flex items-center justify-between py-2 text-sm">
          <span className="text-zinc-400">手機推播通知</span>
          <Switch checked={false} disabled />
        </div>
        <p className="mt-1 rounded-xl bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          🔒 手機推播是<b>付費會員</b>專屬功能。升級後,新專欄與分析發布時會即時通知你。
          <button onClick={() => setModal(true)} className="ml-1 font-semibold text-brand underline">
            了解方案
          </button>
        </p>

        {modal && (
          <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
            <div className="absolute inset-0 bg-black/45" onClick={() => setModal(false)} />
            <div className="relative z-10 max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-5 pb-8 shadow-2xl dark:bg-zinc-900 sm:rounded-3xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold">手機推播通知</h3>
                <button
                  onClick={() => setModal(false)}
                  aria-label="關閉"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 dark:bg-zinc-800"
                >
                  <X size={16} />
                </button>
              </div>

              <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
                開啟後,你會在以下情況第一時間收到手機推播:
              </p>
              <ul className="mt-2 space-y-1.5 text-sm text-zinc-600 dark:text-zinc-300">
                {['專家老師發布新專欄分析', '你訂閱的市場有重要更新', '每日精選新聞與盤勢提醒'].map((p) => (
                  <li key={p} className="flex items-center gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-emerald-500" /> {p}
                  </li>
                ))}
              </ul>
              <p className="mt-4 rounded-xl bg-zinc-100 px-3 py-2.5 text-xs leading-relaxed text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                此功能為<b className="text-brand">付費會員</b>專屬。升級或開通請聯繫客服,由專人為你設定;開通後即可自由開關手機推播。
              </p>
              <div className="mt-3">
                <SupportButton />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ---- Paid member: functional switch ----
  const hint =
    state === 'unsupported'
      ? '此瀏覽器不支援推播'
      : state === 'need-install'
        ? '請先用 Safari 開啟 → 分享 → 加入主畫面,再從主畫面圖示開啟'
        : state === 'denied'
          ? '通知已被瀏覽器/系統封鎖,請到系統設定開啟後再試'
          : '';

  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-sm">
        <span>手機推播通知</span>
        <Switch
          checked={state === 'on'}
          disabled={state === 'busy' || state === 'unsupported' || state === 'need-install' || state === 'denied'}
          onChange={toggle}
        />
      </div>
      {hint && <p className="mt-1 text-xs leading-relaxed text-zinc-400">{hint}</p>}
      {err && <p className="mt-1 text-xs text-rose-500">{err}</p>}
    </div>
  );
}
